
import React from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormControl, FormField, FormItem, FormLabel } from "@/components/ui/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { toast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";

const formSchema = z.object({
  name: z.string().min(2, "Το όνομα πρέπει να έχει τουλάχιστον 2 χαρακτήρες"),
  email: z.string().email("Παρακαλώ εισάγετε μια έγκυρη διεύθυνση email"),
  description: z.string().min(10, "Η περιγραφή πρέπει να έχει τουλάχιστον 10 χαρακτήρες"),
  businessType: z.string().min(1, "Παρακαλώ επιλέξτε τύπο επιχείρησης"),
  budget: z.string().optional(),
});

const NewContactForm = () => {
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      email: "",
      description: "",
      businessType: "",
      budget: "",
    },
  });

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    try {
      console.log('Attempting to insert business contact:', values);
      
      const { data, error } = await supabase
        .from('business_contact_submissions')
        .insert([{
          name: values.name,
          email: values.email,
          description: values.description,
          business_type: values.businessType,
          budget: values.budget || null
        }]);

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }

      console.log('Business contact submission successful:', data);
      toast({
        title: "Το αίτημα εστάλη επιτυχώς",
        description: "Ο Mike θα επικοινωνήσει μαζί σας σύντομα.",
      });
      form.reset();
    } catch (error) {
      console.error('Error submitting business contact form:', error);
      toast({
        title: "Σφάλμα",
        description: "Κάτι πήγε στραβά. Παρακαλώ δοκιμάστε ξανά.",
        variant: "destructive",
      });
    }
  };

  return (
    <section id="contact-form" className="py-24 bg-dark-200">
      <div className="container mx-auto px-6 md:px-8 lg:px-12">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
              📩 Φόρμα Επικοινωνίας
            </h2>
            <p className="text-xl text-gray-300 leading-relaxed">
              Έχεις επιχείρηση ή κάποια ιδέα; Συμπλήρωσε τη φόρμα και θα επικοινωνήσει άμεσα μαζί σου.
            </p>
          </div>
          
          <div className="bg-dark-300 p-8 md:p-12 rounded-lg">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-white text-lg">Ονοματεπώνυμο</FormLabel>
                        <FormControl>
                          <Input 
                            placeholder="Το όνομά σας" 
                            className="bg-dark-400 border-gray-600 text-white placeholder-gray-400 focus:border-brand focus:ring-brand h-12"
                            {...field} 
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-white text-lg">Email</FormLabel>
                        <FormControl>
                          <Input 
                            type="email"
                            placeholder="email@example.com" 
                            className="bg-dark-400 border-gray-600 text-white placeholder-gray-400 focus:border-brand focus:ring-brand h-12"
                            {...field} 
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>
                
                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-white text-lg">Περιγραφή ανάγκης / ιδέας</FormLabel>
                      <FormControl>
                        <Textarea 
                          placeholder="Περιγράψτε τι χρειάζεστε ή την ιδέα σας..." 
                          className="bg-dark-400 border-gray-600 text-white placeholder-gray-400 focus:border-brand focus:ring-brand min-h-[120px]"
                          {...field} 
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                
                <div className="grid md:grid-cols-2 gap-6">
                  <FormField
                    control={form.control}
                    name="businessType"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-white text-lg">Είδος επιχείρησης</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger className="bg-dark-400 border-gray-600 text-white h-12">
                              <SelectValue placeholder="Επιλέξτε τύπο επιχείρησης" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent className="bg-dark-400 border-gray-600">
                            <SelectItem value="retail">Λιανικό Εμπόριο</SelectItem>
                            <SelectItem value="services">Υπηρεσίες</SelectItem>
                            <SelectItem value="restaurant">Εστίαση</SelectItem>
                            <SelectItem value="health">Υγεία/Ομορφιά</SelectItem>
                            <SelectItem value="tech">Τεχνολογία</SelectItem>
                            <SelectItem value="education">Εκπαίδευση</SelectItem>
                            <SelectItem value="other">Άλλο</SelectItem>
                          </SelectContent>
                        </Select>
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="budget"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-white text-lg">Budget (προαιρετικό)</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger className="bg-dark-400 border-gray-600 text-white h-12">
                              <SelectValue placeholder="Επιλέξτε budget" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent className="bg-dark-400 border-gray-600">
                            <SelectItem value="under-500">Κάτω από 500€</SelectItem>
                            <SelectItem value="500-1500">500€ - 1.500€</SelectItem>
                            <SelectItem value="1500-3000">1.500€ - 3.000€</SelectItem>
                            <SelectItem value="3000-5000">3.000€ - 5.000€</SelectItem>
                            <SelectItem value="over-5000">Πάνω από 5.000€</SelectItem>
                          </SelectContent>
                        </Select>
                      </FormItem>
                    )}
                  />
                </div>
                
                <div className="pt-4">
                  <Button 
                    type="submit" 
                    className="w-full bg-brand hover:bg-brand-hover text-white font-bold text-xl py-6 rounded-lg shadow-lg hover:shadow-xl transition-all"
                  >
                    Αποστολή αιτήματος
                  </Button>
                </div>
              </form>
            </Form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default NewContactForm;
